import {type PIXI} from '@colonist/pixi'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler.ts'
import {MovingSpriteView} from '../../ui_canvas_engine/MovingSpriteView.ts'
import {UIGameMapEdgeController} from './UIGameMapEdgeController.ts'
import {type TileEdge, Point} from '@colonist/game-mechanic-validators'
import {TileEdgeDirection} from '@colonist/game-states'
import {SpriteView} from '../../ui_canvas_engine/SpriteView.ts'
import {type UIGameManager} from '../UIGameManager.ts'
import {type ToolTipText} from '../ui_game_tooltip/UIGameToolTipController.ts'

export class UIGameMapRoadView {
    private readonly uiGameManager: UIGameManager

    private readonly roadPosition: Point
    private readonly view: MovingSpriteView
    private readonly roadHighlight: SpriteView
    edge: TileEdge

    constructor(uiGameManaer: UIGameManager, edge: TileEdge, mapCenter: Point, hexHeight: number, container: PIXI.Container, drawingExistingEdges: boolean) {
        if(edge.owner == undefined) return

        this.uiGameManager = uiGameManaer

        this.edge = edge
        const roadTexture = UIGameSpriteHandler.getRoadForPlayer(edge.owner)
        const scalingToFillGapsBetweenRoads = 1.01
        const roadAspectRatio = roadTexture.width / roadTexture.height
        const height = hexHeight / 2 * scalingToFillGapsBetweenRoads
        const width = height * roadAspectRatio

        const edgeCenter = edge.hexEdge.centerPointToPixel(mapCenter, hexHeight / 2)
        this.roadPosition = new Point(edgeCenter.x, edgeCenter.y)
        const animationYPoint = drawingExistingEdges ? this.roadPosition.y : this.roadPosition.y - hexHeight
        const animationStartP1 = new Point(this.roadPosition.x, animationYPoint)
        this.view = new MovingSpriteView(animationStartP1.x, animationStartP1.y, width, height, container, roadTexture)
        this.view.pivot.set(0.5, 0.5)
        this.view.anchor.set(0.5, 0.5)

        const roadHighlightTexture = UIGameSpriteHandler.getRoadHighlight()
        const highlightAspectRatio = roadTexture.width / roadTexture.height
        const roadHighlightHeight = height * 1.08
        const roadHighlightWidth = roadHighlightHeight * highlightAspectRatio * 1.23

        this.roadHighlight = new SpriteView(this.roadPosition.x, this.roadPosition.y, roadHighlightWidth, roadHighlightHeight, container, roadHighlightTexture)
        this.roadHighlight.pivot.set(0.5, 0.5)
        this.roadHighlight.anchor.set(0.5, 0.5)
        this.deactivateHighlight()

        this.setLayer()

        this.view.rotate(this.getRotationAngle())
        this.roadHighlight.rotate(this.getRotationAngle())
    }

    private getRotationAngle(): number {
        switch(this.edge.hexEdge.z) {
            case TileEdgeDirection.NorthWest: return 240
            case TileEdgeDirection.West: return 0
            case TileEdgeDirection.SouthWest: return 120
        }
    }

    executeStartAnimation(edge: TileEdge, drawingExistingEdges: boolean, pointerover: (event) => void, pointerout: (event) => void, toolTipText: ToolTipText) {
        const animationCallback = drawingExistingEdges ? () => {
            this.view.setHoverAction(pointerover, pointerout)
            this.view.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [], toolTipText)
        } : () => {
            UIGameMapEdgeController.playEdgeSound(edge)
            this.view.setHoverAction(pointerover, pointerout)
            this.view.setTip({key: 'strings:colonistCommon.utils.emptyString'}, [], toolTipText)
        }

        this.view.animationController.animationCompleteCallBack = animationCallback
        this.view.animationController.animateTo(this.roadPosition)
    }

    removeView() {
        if(this.view.parent == undefined) return
        this.view.parent.removeChild(this.view)
        if(this.roadHighlight.parent != undefined) this.roadHighlight.parent.removeChild(this.roadHighlight)
    }

    activateHighlight() {
        this.roadHighlight.visible = true
    }

    deactivateHighlight() {
        this.roadHighlight.visible = false
    }

    setLayer(offset: number = 0) {
        const layer = this.uiGameManager.mapController.mapView.layers.mapEdgePieces
        this.view.zIndex = layer + offset
        this.roadHighlight.zIndex = layer - 0.05 + offset
    }
}
